/**
 * Manages the inactive insurance write-back state and effects.
 * Handles business office report generation for inactive insurance cases.
 */
import { useEffect, useState } from "react";
import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../../../../../data-model/busniessOfficeNote";
import { BusniessOfficeReportType } from "../../../../../enums/busniessOfficeReportType";
import { generateBusniessOfficeReportForInactiveInsurance } from "../../../helpers/generateBusniessOfficeReportForInactiveInsurance";

const DEFAULT_INACTIVE_COMMENT =
  "Authmate Case \n\nInsurance is inactive, please check";

/**
 * Encapsulates the inactive insurance write-back state: the write-back notes,
 * query type, comment change handler, and the auto-initialization effect
 * when reports change.
 *
 * @param authReports - Business-office reports from Postgres auth-on-file data
 * @returns Write-back notes, query type, and a handler to update comments
 */
export const useInactiveInsuranceWriteBack = (
  authReports?: BusniessOfficeNote[],
) => {
  const [writeBackNotes, setWriteBackNotes] = useState<AllCommentsProps>({
    comments: [],
    commentDate: "",
    type: BusniessOfficeReportType.FINAL,
  });

  const [queryType, setQueryType] = useState<string>(DEFAULT_INACTIVE_COMMENT);

  /**
   * Updates the query type and regenerates the write-back report.
   *
   * @param comment - The new comment text for the write-back
   */
  const handleCommentsChange = (comment: string) => {
    setQueryType(comment);
    const report = generateBusniessOfficeReportForInactiveInsurance(
      authReports,
      comment,
    );
    if (report) {
      setWriteBackNotes(report);
    }
  };

  useEffect(() => {
    if (authReports?.length) {
      const report = generateBusniessOfficeReportForInactiveInsurance(
        authReports,
        DEFAULT_INACTIVE_COMMENT,
      );
      if (report) {
        setWriteBackNotes(report);
      }
    }
  }, [authReports]);

  return { writeBackNotes, queryType, handleCommentsChange };
};

/** Return type of the useInactiveInsuranceWriteBack hook. */
export type InactiveInsuranceWriteBack = ReturnType<
  typeof useInactiveInsuranceWriteBack
>;
